import { useState, useEffect, useMemo } from 'react';
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../types/admin';
import {
  ORPCounselorProfile,
  ORPJobSeeker,
  ORPAlert,
  ORPDashboardStats,
  ORPConsolidatedStats,
  CVScorePoint,
  ORPAlertType,
} from '../types/orp.types';

// ============================================================================
// useORPRole — Check if current user is an ORP counselor
// ============================================================================

export const useORPRole = () => {
  const { user } = useAuth();
  const [isORPCounselor, setIsORPCounselor] = useState(false);
  const [counselorProfile, setCounselorProfile] = useState<ORPCounselorProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!user) {
      setIsORPCounselor(false);
      setCounselorProfile(null);
      setLoading(false);
      return;
    }

    const fetchProfile = async () => {
      try {
        setLoading(true);

        // Check user role
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);

        if (!userDoc.exists() || userDoc.data().role !== 'orp_counselor') {
          setIsORPCounselor(false);
          setCounselorProfile(null);
          setLoading(false);
          return;
        }

        setIsORPCounselor(true);

        // Fetch ORP counselor profile
        const counselorDocRef = doc(db, 'orpCounselors', user.uid);
        const counselorDoc = await getDoc(counselorDocRef);

        if (counselorDoc.exists()) {
          setCounselorProfile({
            id: counselorDoc.id,
            ...counselorDoc.data(),
          } as ORPCounselorProfile);
        }

        setLoading(false);
      } catch (err) {
        setError(err as Error);
        setLoading(false);
      }
    };

    fetchProfile();
  }, [user]);

  return { isORPCounselor, counselorProfile, loading, error };
};

// ============================================================================
// useORPJobSeekers — Fetch assigned job seekers with real-time updates
// ============================================================================

export const useORPJobSeekers = () => {
  const { user } = useAuth();
  const [jobSeekers, setJobSeekers] = useState<ORPJobSeeker[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!user) {
      setJobSeekers([]);
      setLoading(false);
      return;
    }

    // Listen to the orpAssignments subcollection for real-time updates
    const assignmentsRef = collection(db, 'orpCounselors', user.uid, 'assignments');
    const q = query(assignmentsRef);

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const seekers: ORPJobSeeker[] = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();

          // Generate alerts based on data
          const alerts = generateAlerts(docSnap.id, data);

          return {
            id: docSnap.id,
            displayName: data.displayName || 'Demandeur',
            email: data.email || '',
            photoURL: data.photoURL,
            status: data.status || 'active',
            registrationDate: data.registrationDate || Timestamp.now(),
            lastActivityAt: data.lastActivityAt,
            currentCVScore: data.currentCVScore ?? 0,
            cvScoreHistory: (data.cvScoreHistory || []) as CVScorePoint[],
            activeApplications: data.activeApplications ?? 0,
            totalApplications: data.totalApplications ?? 0,
            sector: data.sector,
            targetRole: data.targetRole,
            alerts,
            isAnonymized: data.isAnonymized,
            anonymizedId: data.anonymizedId,
          } as ORPJobSeeker;
        });

        setJobSeekers(seekers);
        setLoading(false);
      },
      (err) => {
        setError(err as Error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  return { jobSeekers, loading, error };
};

// ============================================================================
// useORPAlerts — Aggregate alerts across all job seekers
// ============================================================================

export const useORPAlerts = (jobSeekers: ORPJobSeeker[]) => {
  const alerts = useMemo(() => {
    const allAlerts: ORPAlert[] = [];
    for (const seeker of jobSeekers) {
      allAlerts.push(...seeker.alerts);
    }
    // Sort by severity (high first) then by date (newest first)
    return allAlerts.sort((a, b) => {
      const severityOrder = { high: 0, medium: 1, low: 2 };
      const sevDiff = severityOrder[a.severity] - severityOrder[b.severity];
      if (sevDiff !== 0) return sevDiff;
      return b.createdAt.toMillis() - a.createdAt.toMillis();
    });
  }, [jobSeekers]);

  const unreadCount = alerts.filter((a) => !a.isRead).length;

  return { alerts, unreadCount };
};

// ============================================================================
// useORPStats — Compute dashboard statistics
// ============================================================================

export const useORPStats = (jobSeekers: ORPJobSeeker[]): ORPDashboardStats => {
  return useMemo(() => {
    const active = jobSeekers.filter((s) => s.status === 'active');
    const placed = jobSeekers.filter((s) => s.status === 'placed');
    const inactive = jobSeekers.filter((s) => s.status === 'inactive');
    const totalScore = active.reduce((sum, s) => sum + s.currentCVScore, 0);
    const totalApps = jobSeekers.reduce((sum, s) => sum + s.activeApplications, 0);
    const alertCount = jobSeekers.reduce((sum, s) => sum + s.alerts.length, 0);

    return {
      totalAssigned: jobSeekers.length,
      activeJobSeekers: active.length,
      placedJobSeekers: placed.length,
      inactiveJobSeekers: inactive.length,
      averageCVScore: active.length > 0 ? Math.round(totalScore / active.length) : 0,
      totalActiveApplications: totalApps,
      alertCount,
    };
  }, [jobSeekers]);
};

// ============================================================================
// useORPConsolidatedStats — Phase 2: Consolidated statistics for SECO reporting
// ============================================================================

export const useORPConsolidatedStats = (jobSeekers: ORPJobSeeker[]): ORPConsolidatedStats => {
  return useMemo(() => {
    const now = Date.now();
    const total = jobSeekers.length;
    const placed = jobSeekers.filter((s) => s.status === 'placed');

    // Placement rate
    const placementRate = total > 0 ? Math.round((placed.length / total) * 100) : 0;

    // Average unemployment duration (days since registration for active seekers)
    const activeSeekers = jobSeekers.filter((s) => s.status === 'active');
    const totalDays = activeSeekers.reduce((sum, s) => {
      const regDate = s.registrationDate?.toMillis?.() || now;
      return sum + (now - regDate) / (1000 * 60 * 60 * 24);
    }, 0);
    const averageUnemploymentDays = activeSeekers.length > 0
      ? Math.round(totalDays / activeSeekers.length)
      : 0;

    // Average score by sector
    const sectorMap = new Map<string, { totalScore: number; count: number }>();
    for (const s of jobSeekers) {
      const sector = s.sector || 'Non spécifié';
      const existing = sectorMap.get(sector) || { totalScore: 0, count: 0 };
      existing.totalScore += s.currentCVScore;
      existing.count += 1;
      sectorMap.set(sector, existing);
    }
    const averageScoreBySector = Array.from(sectorMap.entries())
      .map(([sector, { totalScore, count }]) => ({
        sector,
        avgScore: Math.round(totalScore / count),
        count,
      }))
      .sort((a, b) => b.count - a.count);

    // Score distribution
    const scoreDistribution = [
      { range: '0–50', count: jobSeekers.filter((s) => s.currentCVScore < 50).length },
      { range: '50–70', count: jobSeekers.filter((s) => s.currentCVScore >= 50 && s.currentCVScore < 70).length },
      { range: '70–100', count: jobSeekers.filter((s) => s.currentCVScore >= 70).length },
    ];

    // Status breakdown
    const statusCounts = new Map<string, number>();
    for (const s of jobSeekers) {
      statusCounts.set(s.status, (statusCounts.get(s.status) || 0) + 1);
    }
    const statusBreakdown = Array.from(statusCounts.entries()).map(([status, count]) => ({
      status: status as ORPJobSeeker['status'],
      count,
      percentage: total > 0 ? Math.round((count / total) * 100) : 0,
    }));

    return {
      placementRate,
      averageUnemploymentDays,
      averageScoreBySector,
      scoreDistribution,
      statusBreakdown,
    };
  }, [jobSeekers]);
};

// ============================================================================
// HELPERS
// ============================================================================

function generateAlerts(
  jobSeekerId: string,
  data: Record<string, any>
): ORPAlert[] {
  const alerts: ORPAlert[] = [];
  const now = Date.now();
  const name = data.displayName || data.anonymizedId || 'Demandeur';

  // Inactivity alert: > 14 days
  if (data.lastActivityAt) {
    const lastActivity = data.lastActivityAt.toMillis
      ? data.lastActivityAt.toMillis()
      : data.lastActivityAt;
    const daysSinceActivity = (now - lastActivity) / (1000 * 60 * 60 * 24);

    if (daysSinceActivity > 14) {
      alerts.push({
        id: `inactivity-${jobSeekerId}`,
        jobSeekerId,
        jobSeekerName: name,
        type: 'inactivity' as ORPAlertType,
        severity: daysSinceActivity > 30 ? 'high' : 'medium',
        message: `Inactif depuis ${Math.floor(daysSinceActivity)} jours`,
        isRead: false,
        createdAt: Timestamp.now(),
      });
    }
  }

  // Score decline alert
  const history = (data.cvScoreHistory || []) as CVScorePoint[];
  if (history.length >= 2) {
    const sorted = [...history].sort(
      (a, b) => (b.date?.toMillis?.() || 0) - (a.date?.toMillis?.() || 0)
    );
    if (sorted.length >= 2) {
      const latest = sorted[0].score;
      const previous = sorted[1].score;
      const decline = previous - latest;

      if (decline >= 10) {
        alerts.push({
          id: `score-decline-${jobSeekerId}`,
          jobSeekerId,
          jobSeekerName: name,
          type: 'score_decline' as ORPAlertType,
          severity: decline >= 20 ? 'high' : 'medium',
          message: `Score CV en baisse de ${decline} points (${previous} → ${latest})`,
          isRead: false,
          createdAt: Timestamp.now(),
        });
      }
    }
  }

  // Interview soon alert (< 48h)
  if (data.nextInterviewAt) {
    const interviewTime = data.nextInterviewAt.toMillis
      ? data.nextInterviewAt.toMillis()
      : data.nextInterviewAt;
    const hoursUntil = (interviewTime - now) / (1000 * 60 * 60);

    if (hoursUntil > 0 && hoursUntil <= 48) {
      alerts.push({
        id: `interview-${jobSeekerId}`,
        jobSeekerId,
        jobSeekerName: name,
        type: 'interview_soon' as ORPAlertType,
        severity: hoursUntil <= 12 ? 'high' : 'low',
        message: `Entretien dans ${Math.floor(hoursUntil)}h`,
        isRead: false,
        createdAt: Timestamp.now(),
      });
    }
  }

  return alerts;
}
